import { defaultTheme } from "react-admin";
import type { ThemeOptions } from "@mui/material";

/** Uşaq Qaynar Xətti — same purple as mobile app */
export const brandColors = {
  purple: "#9c5ebf",
  purpleDark: "#7a3fa0",
  purpleLight: "#b88fd4",
  purpleUltraLight: "#f5ebff",
  purpleBorder: "#ecd0ff",
};

const sharedComponents: ThemeOptions["components"] = {
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        color: brandColors.purple,
        minWidth: 36,
      },
    },
  },
  RaMenuItemLink: {
    styleOverrides: {
      root: {
        "& .MuiSvgIcon-root": {
          color: brandColors.purple,
        },
        "&.RaMenuItemLink-active": {
          borderLeftColor: brandColors.purple,
          backgroundColor: brandColors.purpleUltraLight,
          "& .MuiListItemText-primary": {
            color: brandColors.purpleDark,
            fontWeight: 600,
          },
          "& .MuiSvgIcon-root": {
            color: brandColors.purpleDark,
          },
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      containedPrimary: {
        backgroundColor: brandColors.purple,
        "&:hover": {
          backgroundColor: brandColors.purpleDark,
        },
      },
      outlinedPrimary: {
        borderColor: brandColors.purple,
        color: brandColors.purple,
        "&:hover": {
          borderColor: brandColors.purpleDark,
          backgroundColor: brandColors.purpleUltraLight,
        },
      },
      textPrimary: {
        color: brandColors.purple,
        "&:hover": {
          backgroundColor: brandColors.purpleUltraLight,
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      colorPrimary: {
        color: brandColors.purple,
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        "&.Mui-checked": {
          color: brandColors.purple,
        },
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      switchBase: {
        "&.Mui-checked": {
          color: brandColors.purple,
          "& + .MuiSwitch-track": {
            backgroundColor: brandColors.purpleLight,
          },
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        "&.Mui-selected": {
          color: brandColors.purple,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: brandColors.purple,
      },
    },
  },
  RaLogin: {
    styleOverrides: {
      avatar: {
        backgroundColor: brandColors.purpleUltraLight,
      },
      icon: {
        backgroundColor: "transparent",
      },
    },
  },
};

const baseLight: ThemeOptions = {
  ...defaultTheme,
  palette: {
    mode: "light",
    primary: {
      main: brandColors.purple,
      light: brandColors.purpleLight,
      dark: brandColors.purpleDark,
      contrastText: "#fff",
    },
    secondary: {
      main: brandColors.purpleLight,
      contrastText: brandColors.purpleDark,
    },
    success: { main: "#16a34a" },
    error: { main: "#dc2626" },
    warning: { main: "#f59e0b" },
    background: { default: "#f5f6fb", paper: "#ffffff" },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  components: {
    ...defaultTheme.components,
    ...sharedComponents,
  },
};

const baseDark: ThemeOptions = {
  ...defaultTheme,
  palette: {
    mode: "dark",
    primary: {
      main: brandColors.purpleLight,
      light: "#d4b8f0",
      dark: brandColors.purple,
      contrastText: "#0b0b14",
    },
    secondary: {
      main: brandColors.purple,
      contrastText: "#fff",
    },
    background: { default: "#0e1117", paper: "#161b22" },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  components: {
    ...defaultTheme.components,
    ...sharedComponents,
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: brandColors.purpleLight,
          minWidth: 36,
        },
      },
    },
    RaMenuItemLink: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: brandColors.purpleLight,
          },
          "&.RaMenuItemLink-active": {
            borderLeftColor: brandColors.purpleLight,
            backgroundColor: "rgba(156, 94, 191, 0.15)",
            "& .MuiListItemText-primary": {
              color: brandColors.purpleLight,
              fontWeight: 600,
            },
          },
        },
      },
    },
  },
};

export const lightTheme = baseLight;
export const darkTheme = baseDark;
